import { useEffect, useRef } from "react";

import { generateColourVariants } from "../../utils/generateColourVariants";

import { LoadingSpinnerProps, sizeMap } from "./LoadingSpinner.type";

const colourMap = generateColourVariants();

/**
 * `LoadingSpinner` is a circular animated SVG spinner component used to indicate loading states.
 *
 * It consists of a faint elliptical base ring and a rotating arc with a gradient tail to create
 * a smooth and dynamic motion effect. This component respects the design system’s colour tokens
 * via the `variant` prop and scales based on a customisable `size` value.
 *
 * @example
 * ```tsx
 * <LoadingSpinner />
 * <LoadingSpinner size={80} variant="primary-dark" />
 * ```
 *
 * @param {object} props - Component props
 * @param {"xs" | "sm" | "md" | "lg" | "xl"} size - Size of the spinner. Height is calculate proportionally based on the width.
 * - `'xs'` = 20px
 * - `'sm'` = 80px
 * - `'md'` = 120px
 * - `'lg'` = 160px
 * - `'xl'` = 200px
 *
 * @param {ColourVariant} [colour] - A design system colour token used for the arc gradient.
 *
 * @returns {JSX.Element} Animated SVG loading spinner
 */

export const LoadingSpinner = ({
  size = "lg",
  colour = "accent-deep"
}: LoadingSpinnerProps): JSX.Element => {
  const ref = useRef<SVGGElement>(null);

  useEffect(() => {
    if (!ref.current) return;

    let start: number | null = null;
    let frame: number;

    const animate = (timestamp: number) => {
      if (!start) start = timestamp;
      const progress = timestamp - start;
      const rotation = (progress / 1000) * 360;
      if (ref.current) {
        ref.current.setAttribute(
          "transform",
          `rotate(${rotation % 360}, 76.9125, 78)`
        );
      }
      frame = requestAnimationFrame(animate);
    };

    frame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frame);
  }, [ref]);

  const pixelSize = sizeMap[size];
  // Allows for proper resizing
  const aspectRatio = 156 / 154;
  const svgWidth = pixelSize;
  const svgHeight = pixelSize * aspectRatio;

  const spinnerColour = colourMap[colour].color;
  const gradientId = `spinner-gradient-${spinnerColour}`;
  const baseStroke = `${spinnerColour}40`; // add opacity for base ring

  return (
    <svg
      data-testid="loading-spinner"
      width={svgWidth}
      height={svgHeight}
      viewBox="0 0 154 156"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <ellipse
        cx="76.9125"
        cy="78"
        rx="56.2232"
        ry="57.0181"
        stroke={baseStroke}
        strokeWidth="14"
      />
      <g data-testid="spinner-arc" ref={ref}>
        <path
          d="M76.5233 20.9833C91.4342 20.8786 105.775 26.7853 116.392 37.4039C127.009 48.0226 133.031 62.4834 133.134 77.6052"
          stroke={`url(#${gradientId})`}
          strokeWidth="14"
          strokeLinecap="round"
          strokeDasharray="4 4"
        />
      </g>
      <defs>
        <linearGradient
          id={gradientId}
          x1="125.45"
          y1="106.776"
          x2="27.6853"
          y2="50.4202"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={spinnerColour} />
          <stop
            offset="0.755208"
            stopColor={spinnerColour}
            stopOpacity="0.01"
          />
          <stop offset="1" stopColor={spinnerColour} stopOpacity="0" />
        </linearGradient>
      </defs>
    </svg>
  );
};
